import type { ServiceItem } from '../../types';
import type { ServiceTileType } from '../../services/serviceTypeMatch';
import { BEGRUENDUNG_MINDESTLAENGE, type AusgabeUnterlage } from '../../services/unterlagenAusgabenService';
import { calculateStats } from '../project-hub/calculateStats';
import { FOLGEUNTERLAGEN, type Folgeunterlage } from '../../services/projektSteckbrief/steckbrief';
import type { PflichtArt, PflichtEintrag } from './types';
import { datumDeutsch } from './unterlagenLogic';

/**
 * Reiter „Übergabe" der Planungsphase — pure Regeln (Automatisierungsplan,
 * Stufe 6.2).
 *
 * Je Folgeunterlage EINE Zeile: Pflicht, Stand, letzte Entscheidung des
 * Aenderungsdiensts, LV-Bezug und das Sprungziel ins Modul. Hier wird nichts
 * geschrieben und nichts gebucht — gebucht wird weiter in den Modulen und
 * ihren Dialogen; dieser Reiter liest nur zusammen, was dort steht.
 *
 * ═══ QUELLEN ══════════════════════════════════════════════════════════════
 *   pflichten  — `pflichtenStand` (live aus den gespeicherten Merkmalen),
 *                dreiwertig mit Begruendung. Handbuch und Baustellenordnung
 *                haben keine gesetzliche Pflicht: dort steht „nach Auftrag".
 *   ausgaben   — die letzte freigegebene bzw. versendete Fassung je Unterlage
 *                (`projekt_unterlagen_ausgaben`); der Aenderungsdienst traegt
 *                sie an jedem Stand mit (`ausgabenAusStaenden`).
 *   staende    — der Aenderungsdienst (`useAbweichungen`): je Fassung, ob die
 *                Planungsdaten inzwischen abweichen und was dazu entschieden
 *                wurde.
 *   ablage     — die Projektablage; hier zaehlt nur, ob ein als Entwurf
 *                abgelegtes Blatt des Moduls existiert.
 *   lv         — die Leistungsliste des Projekts (`calculateStats`).
 *
 * ═══ ZWEI WERTEMENGEN FUER DIESELBEN UNTERLAGEN ═══════════════════════════
 * Der Steckbrief sagt `ufsa` und `bauordnung` (`Folgeunterlage`), Ablage und
 * Ausgabentabelle sagen `unterlage` und `baustellenordnung`
 * (`AusgabeUnterlage`, zugleich `erzeugt_aus`). Die Zeilen laufen ueber die
 * `Folgeunterlage`; `AUSGABE_JE_UNTERLAGE` ist die Bruecke in die andere
 * Welt (dieselbe wie `FOLGEUNTERLAGE_JE_AUSGABE` im Aenderungsdienst, nur
 * in Gegenrichtung).
 *
 * ═══ STAND — REIHENFOLGE DER REGELN ═══════════════════════════════════════
 *   unbekannt          der Aenderungsdienst (Ausgaben, Entscheidungen) konnte
 *                      nicht gelesen werden. Steht VOR allem anderen: ein
 *                      Lesefehler darf nie wie „fehlt" oder „aktuell" aussehen.
 *   veraltet           eine offene Abweichung zur geltenden Fassung
 *                      (Anzahl im Text) — gelb, Handlungsbedarf.
 *   versendet /        die letzte Ausgabe mit Datum; `versendet` schlaegt
 *   freigegeben        `freigegeben`, weil Versand die Freigabe voraussetzt.
 *   nicht erforderlich Pflicht = `keine` und keine Ausgabe; ein Entwurf in
 *                      der Ablage wird im Text genannt, aendert den Stand
 *                      aber nicht.
 *   entwurf            nur ein Entwurf in der Ablage (Tag „Entwurf" bzw.
 *                      Titelpraefix aus `unterlagenAblage.ts`).
 *   fehlt              nichts davon.
 *
 * Ein Entwurf wird NUR an der Ablage erkannt. Was ein Modul intern als
 * ungespeicherten oder nicht abgelegten Entwurf haelt, sieht dieser Reiter
 * nicht — wer einen Entwurf hier sehen will, legt ihn ab („Entwurf ablegen",
 * Plan 1A.3). Ein Lesefehler der Ablage kippt den Stand deshalb nicht; er
 * steht als Satz daneben („Entwürfe werden nicht erkannt").
 *
 * ═══ KEIN DOM, KEINE SUPABASE ═════════════════════════════════════════════
 * Pur und in der Node-Umgebung testbar. Die Eingangstypen sind strukturell
 * gehalten (nur die Felder, die hier gelesen werden); die echten Saetze aus
 * `useAbweichungen` (`AbweichungsStand`) und der Ablage (`ProjektDokument`)
 * passen darauf.
 */

// ═══════════════════════════════════════════════════════════════════════════
// ZUORDNUNGEN JE FOLGEUNTERLAGE
// ═══════════════════════════════════════════════════════════════════════════

/** Modulschluessel von `App.executeModuleNavigation` fuer die sechs Folgeunterlagen. */
export type UebergabeSprungziel = 'vorank' | 'unterlage' | 'sigeplan' | 'asplan' | 'bauordnung' | 'handbuch';

export const UNTERLAGE_TITEL: Record<Folgeunterlage, string> = {
    vorankuendigung: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    asplan: 'A+S-Plan',
    ufsa: 'Unterlage für spätere Arbeiten',
    bauordnung: 'Baustellenordnung / HSE-Plan',
    handbuch: 'SiGe-Handbuch',
};

/** Kennung in Ausgabentabelle und Ablage (`erzeugt_aus`) je Folgeunterlage. */
export const AUSGABE_JE_UNTERLAGE: Record<Folgeunterlage, AusgabeUnterlage> = {
    vorankuendigung: 'vorankuendigung',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    ufsa: 'unterlage',
    bauordnung: 'baustellenordnung',
    handbuch: 'handbuch',
};

export const SPRUNGZIEL_JE_UNTERLAGE: Record<Folgeunterlage, UebergabeSprungziel> = {
    vorankuendigung: 'vorank',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    ufsa: 'unterlage',
    bauordnung: 'bauordnung',
    handbuch: 'handbuch',
};

/** Dasselbe Sprungziel, von der Kennung des Aenderungsdiensts aus (Dialog). */
export const SPRUNGZIEL_JE_AUSGABE: Record<AusgabeUnterlage, UebergabeSprungziel> = {
    vorankuendigung: 'vorank',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    unterlage: 'unterlage',
    baustellenordnung: 'bauordnung',
    handbuch: 'handbuch',
};

/** Kachel der Leistungsliste; `null` = die Unterlage hat keine LV-Zuordnung. */
const LV_JE_UNTERLAGE: Record<Folgeunterlage, ServiceTileType | null> = {
    vorankuendigung: 'vorank',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    ufsa: 'unterlage',
    bauordnung: 'site_rules',
    handbuch: null,
};

/** Pflicht aus `pflichtenStand`; `null` = keine gesetzliche Pflicht („nach Auftrag"). */
const PFLICHT_JE_UNTERLAGE: Record<Folgeunterlage, PflichtArt | null> = {
    vorankuendigung: 'vorankuendigung',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    ufsa: 'ufsa',
    bauordnung: null,
    handbuch: null,
};

// ═══════════════════════════════════════════════════════════════════════════
// EINGANG — strukturell, nur die gelesenen Felder
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Ausgabe (Freigabe oder Versand) einer Folgeunterlage. */
export interface UebergabeAusgabe {
    unterlage: AusgabeUnterlage;
    fassung_ref: string | null;
    /** `freigabe` | `versand`, siehe `AusgabeAnlass`. */
    anlass: string;
    erstellt_am: string | null;
    dokument_id?: string | null;
}

/** Eine festgehaltene Entscheidung des Aenderungsdiensts (Stufe 6.1). */
export interface UebergabeEntscheidung {
    /** `neue_fassung` | `nicht_erheblich` | `spaeter`. */
    entscheid: string;
    begruendung?: string | null;
    entschieden_am?: string | null;
    entschieden_von_name?: string | null;
}

/** Ein Stand des Aenderungsdiensts: eine Fassung gegen die heutigen Planungsdaten. */
export interface UebergabeAbweichungsStand {
    unterlage: AusgabeUnterlage;
    ausgabe: Pick<UebergabeAusgabe, 'fassung_ref' | 'anlass' | 'erstellt_am'>;
    /** Nur die Anzahl wird gelesen. */
    abweichungen: readonly unknown[];
    entscheidung: UebergabeEntscheidung | null;
    /** Abweichung ohne Entscheidung fuer genau diese Abweichung. */
    offen: boolean;
}

/** Eine Zeile der Projektablage, soweit sie hier gelesen wird. */
export interface UebergabeAblageZeile {
    erzeugt_aus?: string | null;
    tags: readonly string[];
    titel: string;
    created_at: string;
}

/** Was beim Laden nicht gelesen werden konnte — je Quelle ein Satz, `null` = gelesen. */
export interface UebergabeLesefehler {
    /** `useAbweichungen.lesefehler`: Steckbrief, Ausgaben, Entscheidungen. */
    aenderungsdienst: string | null;
    ablage: string | null;
}

export const OHNE_UEBERGABE_LESEFEHLER: UebergabeLesefehler = { aenderungsdienst: null, ablage: null };

// ═══════════════════════════════════════════════════════════════════════════
// ERGEBNIS
// ═══════════════════════════════════════════════════════════════════════════

export type UebergabePflichtStand = 'pflicht' | 'keine' | 'ungeklaert' | 'nach_auftrag';

export interface UebergabePflicht {
    stand: UebergabePflichtStand;
    /** Kurzform fuer den Chip. */
    text: string;
    begruendung: string;
    rechtsgrundlage: string;
}

export type UebergabeStandArt =
    | 'fehlt' | 'entwurf' | 'freigegeben' | 'versendet' | 'veraltet' | 'nicht_erforderlich' | 'unbekannt';

export interface UebergabeStand {
    art: UebergabeStandArt;
    /** Ein Satz fuer die Zeile — ohne Platzhalterstriche fuer fehlende Werte. */
    text: string;
    /** ISO-Zeitpunkt der zugrunde liegenden Ausgabe bzw. des Entwurfs, sonst `null`. */
    datum: string | null;
    fassung: string | null;
    /** Offene Abweichungen; 0, wenn nicht `veraltet`. */
    anzahl: number;
}

export interface UebergabeLv {
    text: string;
    used: number | null;
    contracted: number | null;
}

export interface UebergabeZeile {
    unterlage: Folgeunterlage;
    /** Dieselbe Unterlage in Ausgabentabelle und Ablage. */
    ausgabeUnterlage: AusgabeUnterlage;
    titel: string;
    sprungziel: UebergabeSprungziel;
    pflicht: UebergabePflicht;
    stand: UebergabeStand;
    /** „nicht erheblich: Begründung, am 17.10.2026, von Name" oder `null`. */
    entscheidung: string | null;
    lv: UebergabeLv;
    /** Gelb: eine offene Abweichung wartet auf eine Entscheidung. */
    handlungsbedarf: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

const PFLICHT_TEXT: Record<UebergabePflichtStand, string> = {
    pflicht: 'Pflicht',
    keine: 'keine Pflicht',
    ungeklaert: 'ungeklärt',
    nach_auftrag: 'nach Auftrag',
};

const NACH_AUFTRAG_BEGRUENDUNG =
    'Keine gesetzliche Pflicht nach BaustellV. Die Unterlage wird erstellt, wenn sie beauftragt ist (siehe LV-Bezug).';

/** Pflicht einer Unterlage aus dem Pflichtenstand; `pflichten` = `null` heisst „noch nicht gelesen". */
export const pflichtFuer = (
    unterlage: Folgeunterlage,
    pflichten: readonly PflichtEintrag[] | null,
): UebergabePflicht => {
    const art = PFLICHT_JE_UNTERLAGE[unterlage];
    if (!art) {
        return { stand: 'nach_auftrag', text: PFLICHT_TEXT.nach_auftrag, begruendung: NACH_AUFTRAG_BEGRUENDUNG, rechtsgrundlage: '' };
    }
    if (!pflichten) {
        return { stand: 'ungeklaert', text: PFLICHT_TEXT.ungeklaert, begruendung: 'Der Pflichtenstand liegt noch nicht vor.', rechtsgrundlage: '' };
    }
    const eintrag = pflichten.find(p => p.art === art);
    if (!eintrag) {
        return { stand: 'ungeklaert', text: PFLICHT_TEXT.ungeklaert, begruendung: 'Der Pflichtenstand führt diese Unterlage nicht.', rechtsgrundlage: '' };
    }
    return {
        stand: eintrag.stand,
        text: PFLICHT_TEXT[eintrag.stand],
        begruendung: eintrag.begruendung || '',
        rechtsgrundlage: eintrag.rechtsgrundlage || '',
    };
};

const zeit = (iso: string | null | undefined): number => {
    const t = iso ? new Date(iso).getTime() : NaN;
    return isFinite(t) ? t : -Infinity;
};

/**
 * Die Ausgaben, die der Aenderungsdienst an seinen Staenden mitfuehrt — eine
 * je Unterlage mit geltender Fassung. Eine zweite Abfrage der Ausgabentabelle
 * waere ein zweiter Weg zu denselben Daten.
 */
export const ausgabenAusStaenden = (staende: readonly UebergabeAbweichungsStand[]): UebergabeAusgabe[] =>
    staende.map(s => ({
        unterlage: s.unterlage,
        fassung_ref: s.ausgabe.fassung_ref,
        anlass: s.ausgabe.anlass,
        erstellt_am: s.ausgabe.erstellt_am,
    }));

/** Die juengste Ausgabe einer Unterlage nach `erstellt_am`; ohne Datum zaehlt die Reihenfolge. */
export const letzteAusgabe = (
    ausgaben: readonly UebergabeAusgabe[],
    unterlage: Folgeunterlage,
): UebergabeAusgabe | null => {
    const kennung = AUSGABE_JE_UNTERLAGE[unterlage];
    let beste: UebergabeAusgabe | null = null;
    for (const a of ausgaben) {
        if (a.unterlage !== kennung) continue;
        if (!beste || zeit(a.erstellt_am) >= zeit(beste.erstellt_am)) beste = a;
    }
    return beste;
};

/**
 * Ist eine Fassungskennung fuer Menschen lesbar? UUIDs und rohe
 * ISO-Zeitstempel sagen in einer Zeile nichts — dann steht nur das Datum.
 */
export const fassungKurz = (ref: string | null | undefined): string | null => {
    const r = (ref || '').trim();
    if (!r) return null;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(r)) return null;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(r)) return null;
    return r;
};

/** „Fassung Rev. 2 vom 12.10.2026" — Teile, die fehlen, werden weggelassen. */
export const fassungText = (ausgabe: Pick<UebergabeAusgabe, 'fassung_ref' | 'erstellt_am'>): string => {
    const ref = fassungKurz(ausgabe.fassung_ref);
    const tag = datumDeutsch(ausgabe.erstellt_am);
    const teile = ['Fassung', ref, tag ? `vom ${tag}` : null].filter((t): t is string => !!t);
    return teile.length === 1 ? 'Fassung ohne Kennung und Datum' : teile.join(' ');
};

export const ANLASS_TEXT: Record<string, string> = {
    freigabe: 'freigegeben',
    versand: 'versendet',
};

/** Der juengste als Entwurf abgelegte Satz des Moduls, sonst `null`. */
export const entwurfInAblage = (
    ablage: readonly UebergabeAblageZeile[],
    modul: AusgabeUnterlage,
): UebergabeAblageZeile | null => {
    let bester: UebergabeAblageZeile | null = null;
    for (const z of ablage) {
        if (z.erzeugt_aus !== modul) continue;
        const istEntwurf = (z.tags || []).some(t => t.trim().toLowerCase() === 'entwurf') || /^entwurf\b/i.test(z.titel || '');
        if (!istEntwurf) continue;
        if (!bester || zeit(z.created_at) >= zeit(bester.created_at)) bester = z;
    }
    return bester;
};

/** Summe der Abweichungen ueber alle offenen Staende einer Unterlage. */
export const offeneAbweichungen = (
    staende: readonly UebergabeAbweichungsStand[],
    unterlage: Folgeunterlage,
): number => {
    const kennung = AUSGABE_JE_UNTERLAGE[unterlage];
    return staende
        .filter(s => s.unterlage === kennung && s.offen)
        .reduce((n, s) => n + Math.max(1, s.abweichungen.length), 0);
};

const ENTSCHEID_TEXT: Record<string, string> = {
    nicht_erheblich: 'nicht erheblich',
    neue_fassung: 'neue Fassung angelegt',
    spaeter: 'zurückgestellt',
};

/** „nicht erheblich: Begründung, am 17.10.2026, von Name" — nur die Teile, die es gibt. */
export const entscheidungText = (e: UebergabeEntscheidung | null | undefined): string | null => {
    if (!e || !e.entscheid) return null;
    let text = ENTSCHEID_TEXT[e.entscheid] || e.entscheid;
    const begruendung = (e.begruendung || '').trim();
    if (begruendung) text += `: ${begruendung}`;
    const tag = datumDeutsch(e.entschieden_am);
    if (tag) text += `, am ${tag}`;
    const von = (e.entschieden_von_name || '').trim();
    if (von) text += `, von ${von}`;
    return text;
};

/** Die juengste festgehaltene Entscheidung zu einer Unterlage — offen oder nicht. */
export const letzteEntscheidung = (
    staende: readonly UebergabeAbweichungsStand[],
    unterlage: Folgeunterlage,
): UebergabeEntscheidung | null => {
    const kennung = AUSGABE_JE_UNTERLAGE[unterlage];
    let beste: UebergabeEntscheidung | null = null;
    for (const s of staende) {
        if (s.unterlage !== kennung || !s.entscheidung) continue;
        if (!beste || zeit(s.entscheidung.entschieden_am) >= zeit(beste.entschieden_am)) beste = s.entscheidung;
    }
    return beste;
};

export const lvBezug = (lv: readonly ServiceItem[], unterlage: Folgeunterlage): UebergabeLv => {
    const typ = LV_JE_UNTERLAGE[unterlage];
    if (!typ) return { text: 'kein LV-Bezug', used: null, contracted: null };
    const stats = calculateStats([...lv], typ);
    if (!stats) return { text: 'keine LV-Position', used: null, contracted: null };
    return { text: `${stats.used} von ${stats.contracted} gebucht`, used: stats.used, contracted: stats.contracted };
};

// ═══════════════════════════════════════════════════════════════════════════
// STAND
// ═══════════════════════════════════════════════════════════════════════════

const anzahlText = (n: number) => (n === 1 ? '1 Abweichung' : `${n} Abweichungen`);

export const standFuer = (
    unterlage: Folgeunterlage,
    pflicht: UebergabePflicht,
    ausgaben: readonly UebergabeAusgabe[],
    staende: readonly UebergabeAbweichungsStand[],
    ablage: readonly UebergabeAblageZeile[],
    lesefehler: UebergabeLesefehler = OHNE_UEBERGABE_LESEFEHLER,
): UebergabeStand => {
    if (lesefehler.aenderungsdienst) {
        return { art: 'unbekannt', text: `Stand nicht lesbar: ${lesefehler.aenderungsdienst}`, datum: null, fassung: null, anzahl: 0 };
    }

    const kennung = AUSGABE_JE_UNTERLAGE[unterlage];
    const ausgabe = letzteAusgabe(ausgaben, unterlage);
    const offen = offeneAbweichungen(staende, unterlage);
    if (offen > 0) {
        // Die Fassung aus dem Aenderungsdienst ist die, gegen die verglichen
        // wurde — sie zaehlt, auch wenn die Ausgabenliste etwas anderes sagt.
        const stand = staende.find(s => s.unterlage === kennung && s.offen);
        const bezug = stand ? stand.ausgabe : ausgabe;
        return {
            art: 'veraltet',
            text: `veraltet: ${anzahlText(offen)} zur geltenden ${bezug ? fassungText(bezug) : 'Fassung'}`,
            datum: bezug ? bezug.erstellt_am : null,
            fassung: bezug ? bezug.fassung_ref : null,
            anzahl: offen,
        };
    }

    if (ausgabe) {
        const art: UebergabeStandArt = ausgabe.anlass === 'versand' ? 'versendet' : 'freigegeben';
        const tag = datumDeutsch(ausgabe.erstellt_am);
        const ref = fassungKurz(ausgabe.fassung_ref);
        const text = `${ANLASS_TEXT[art === 'versendet' ? 'versand' : 'freigabe']}${tag ? ` am ${tag}` : ' (ohne Datum)'}${ref ? ` · ${ref}` : ''}`;
        return { art, text, datum: ausgabe.erstellt_am, fassung: ausgabe.fassung_ref, anzahl: 0 };
    }

    const entwurf = entwurfInAblage(ablage, kennung);
    if (pflicht.stand === 'keine') {
        const grund = pflicht.begruendung ? `: ${pflicht.begruendung}` : '';
        return {
            art: 'nicht_erforderlich',
            text: `nicht erforderlich${grund}${entwurf ? ' Ein Entwurf liegt trotzdem in der Ablage.' : ''}`,
            datum: null,
            fassung: null,
            anzahl: 0,
        };
    }

    if (entwurf) {
        const tag = datumDeutsch(entwurf.created_at);
        return {
            art: 'entwurf',
            text: `Entwurf in der Ablage${tag ? ` vom ${tag}` : ''}, noch nicht freigegeben`,
            datum: entwurf.created_at,
            fassung: null,
            anzahl: 0,
        };
    }

    return { art: 'fehlt', text: 'fehlt: weder freigegeben noch als Entwurf abgelegt', datum: null, fassung: null, anzahl: 0 };
};

// ═══════════════════════════════════════════════════════════════════════════
// ZEILEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die sechs Zeilen des Reiters in fester Reihenfolge (`FOLGEUNTERLAGEN`).
 * `pflichten` = `null`, solange der Pflichtenstand nicht gelesen ist — dann
 * steht dort „ungeklärt" mit Grund, nie „keine".
 */
export const uebergabeZeilen = (
    pflichten: readonly PflichtEintrag[] | null,
    ausgaben: readonly UebergabeAusgabe[],
    staende: readonly UebergabeAbweichungsStand[],
    ablage: readonly UebergabeAblageZeile[],
    lv: readonly ServiceItem[],
    lesefehler: UebergabeLesefehler = OHNE_UEBERGABE_LESEFEHLER,
): UebergabeZeile[] => FOLGEUNTERLAGEN.map(unterlage => {
    const pflicht = pflichtFuer(unterlage, pflichten);
    const stand = standFuer(unterlage, pflicht, ausgaben, staende, ablage, lesefehler);
    return {
        unterlage,
        ausgabeUnterlage: AUSGABE_JE_UNTERLAGE[unterlage],
        titel: UNTERLAGE_TITEL[unterlage],
        sprungziel: SPRUNGZIEL_JE_UNTERLAGE[unterlage],
        pflicht,
        stand,
        entscheidung: entscheidungText(letzteEntscheidung(staende, unterlage)),
        lv: lvBezug(lv, unterlage),
        handlungsbedarf: stand.art === 'veraltet',
    };
});

/** Zeilen mit offener Abweichung — fuer die Zahl am Reiter. */
export const anzahlVeraltet = (zeilen: readonly UebergabeZeile[]): number =>
    zeilen.filter(z => z.stand.art === 'veraltet').length;

// ═══════════════════════════════════════════════════════════════════════════
// RUECKFRAGE (Stufe 6.1) — Begruendung zu „nicht erheblich"
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Begruendung, wie sie gespeichert wird (Leerraum zusammengezogen) — oder
 * der Grund, warum sie nicht reicht. Dieselbe Mindestlaenge wie der CHECK der
 * Tabelle (`BEGRUENDUNG_MINDESTLAENGE`), damit der Dialog es VOR dem Klick
 * sagt und nicht erst die Datenbank.
 */
export const pruefeRueckfrageBegruendung = (text: string): { wert: string | null; fehler: string | null } => {
    const rein = (text || '').replace(/\s+/g, ' ').trim();
    if (rein.length < BEGRUENDUNG_MINDESTLAENGE) {
        return {
            wert: null,
            fehler: 'Bitte kurz begründen, warum die Änderung für diese Unterlage nicht erheblich ist '
                + `(mindestens ${BEGRUENDUNG_MINDESTLAENGE} Zeichen). Die Begründung ist der Nachweis der Bewertung.`,
        };
    }
    return { wert: rein, fehler: null };
};

/** Die Lesefehler als Saetze fuer den Hinweiskasten; leer, wenn alles gelesen wurde. */
export const lesefehlerSaetze = (lesefehler: UebergabeLesefehler, pflichtenFehler: string | null = null): string[] => {
    const saetze: string[] = [];
    if (lesefehler.aenderungsdienst) {
        saetze.push(`Der Änderungsdienst konnte nicht vollständig gelesen werden: ${lesefehler.aenderungsdienst}`);
    }
    if (lesefehler.ablage) {
        saetze.push(`Die Projektablage konnte nicht gelesen werden: ${lesefehler.ablage} Entwürfe werden deshalb nicht erkannt.`);
    }
    if (pflichtenFehler) saetze.push(`Der Pflichtenstand konnte nicht gelesen werden: ${pflichtenFehler}`);
    return saetze;
};
